import { useState } from "react";
import { Alert, Button, InputText } from "@/deps/ui-library";
import { useChangePassword, useSignOut, useUpdateProfile } from "@/api/queries";
import { useCurrentSession } from "@/app/session";
import { Dialog } from "@/components/Dialog";

export type AccountDialog = "profile" | "password" | "signout";

/** What the account menu opens. Each dialog starts empty, so nothing typed outlives closing it. */
export function AccountDialogs({
  dialog,
  onClose,
}: {
  dialog: AccountDialog | null;
  onClose: () => void;
}) {
  if (dialog === "profile") return <ProfileDialog onClose={onClose} />;
  if (dialog === "password") return <PasswordDialog onClose={onClose} />;
  if (dialog === "signout") return <SignOutDialog onClose={onClose} />;
  return null;
}

function ProfileDialog({ onClose }: { onClose: () => void }) {
  const { session } = useCurrentSession();
  const save = useUpdateProfile();
  const [fullName, setFullName] = useState(session.user.fullName);

  return (
    <Dialog
      open
      onClose={onClose}
      title="Your profile"
      description="Your name appears on documents you prepare or approve."
      footer={
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={fullName.trim() === "" || save.isPending}
          onClick={() => save.mutate({ fullName })}
        >
          {save.isPending ? "Saving" : "Save changes"}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {save.isError ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(save.error as Error).message}
          />
        ) : null}
        <InputText
          label="Full name"
          required
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
        />
        <InputText label="Email" value={session.user.email} readOnly />
        <InputText
          label="Role"
          value={`${session.user.role} at ${session.organization.name}`}
          readOnly
        />
      </div>
    </Dialog>
  );
}

/** Checked here so the person is told which rule; the identity service holds the same ones. */
function passwordProblem(current: string, next: string, confirm: string) {
  if (current === "") return "Enter your current password.";
  if (next.length < 8) return "The new password needs at least 8 characters.";
  if (next === current) return "The new password must differ from the current one.";
  if (next !== confirm) return "The two new passwords do not match.";
  return null;
}

function PasswordDialog({ onClose }: { onClose: () => void }) {
  const change = useChangePassword();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [shown, setShown] = useState<string | null>(null);

  function submit() {
    const problem = passwordProblem(current, next, confirm);
    setShown(problem);
    if (problem === null) {
      change.mutate({ currentPassword: current, newPassword: next }, { onSuccess: onClose });
    }
  }

  const error = shown ?? (change.isError ? (change.error as Error).message : null);

  return (
    <Dialog
      open
      onClose={onClose}
      title="Change password"
      description="This changes your Rigo ID password, for every Rigo product."
      footer={
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={change.isPending}
          onClick={submit}
        >
          {change.isPending ? "Changing" : "Change password"}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {error ? (
          <Alert config={{ variant: "subtle", colorScheme: "danger" }} description={error} />
        ) : null}
        <InputText
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
        <InputText
          label="New password"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
        />
        <InputText
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
        />
      </div>
    </Dialog>
  );
}

function SignOutDialog({ onClose }: { onClose: () => void }) {
  const signOut = useSignOut();
  return (
    <Dialog
      open
      onClose={onClose}
      title="Sign out of Rigo?"
      description="Saved drafts stay where they are. Anything typed into an open document and not saved is lost."
      footer={
        <Button
          colorScheme="danger"
          variant="solid"
          size="sm"
          disabled={signOut.isPending}
          onClick={() => signOut.mutate()}
        >
          Sign out
        </Button>
      }
    >
      {null}
    </Dialog>
  );
}
